import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";

export const CategoryIcon = ({category}) =>
    <img className="anniv2026-category-icon" src={"/public/anniv/2026/categories/" + category.id + ".png"} alt="" draggable={false}/>;

const SLOTS = [
    {index: 0, label: "1er", points: "5 pts"},
    {index: 1, label: "2e", points: "3 pts"},
    {index: 2, label: "3e", points: "1 pt"},
];

const DRAG_THRESHOLD = 8;

/*
 * Podium of a category. Games are moved with pointer events (HTML5 drag and drop does not work on touch screens):
 * - from the pool, a vertical move starts the drag (horizontal moves scroll the pool), a tap fills the first empty slot
 * - from a slot, any move starts the drag; dropping on another slot swaps, dropping elsewhere removes the game
 * Every change is sent right away through setPodium.
 */
export const PodiumModal2026 = ({category, games, podium, setPodium, onClose}) => {

    const fullScreen = useMediaQuery("(max-width:700px)");
    const [drag, setDrag] = useState(undefined);
    const pointer = useRef(undefined);
    const podiumRef = useRef(podium);
    podiumRef.current = podium;

    const gamesById = {};
    games.forEach(game => gamesById[game.id] = game);
    const slots = [0, 1, 2].map(i => podium[i] && gamesById[podium[i]] ? podium[i] : null);
    const pool = games.filter(game => !game.mine && !slots.includes(game.id));

    function slotUnder(x, y) {
        const element = document.elementFromPoint(x, y);
        const slot = element && element.closest("[data-slot]");
        return slot ? parseInt(slot.getAttribute("data-slot")) : undefined;
    }

    function move(gameId, fromSlot, toSlot) {
        const current = [0, 1, 2].map(i => podiumRef.current[i] && gamesById[podiumRef.current[i]] ? podiumRef.current[i] : null);
        const next = [...current];
        if (toSlot === undefined) {
            if (fromSlot === undefined) {
                return;
            }
            next[fromSlot] = null;
        } else if (fromSlot !== undefined) {
            next[fromSlot] = current[toSlot];
            next[toSlot] = gameId;
        } else {
            next[toSlot] = gameId;
        }
        if (next.some((g, i) => g !== current[i])) {
            setPodium(next);
        }
    }

    function onPointerDown(e, gameId, fromSlot) {
        if (e.button !== undefined && e.button !== 0) {
            return;
        }
        pointer.current = {
            gameId, fromSlot,
            startX: e.clientX, startY: e.clientY,
            touch: e.pointerType !== "mouse",
            started: false,
        };
    }

    useEffect(() => {
        function onMove(e) {
            const p = pointer.current;
            if (!p) {
                return;
            }
            const dx = e.clientX - p.startX;
            const dy = e.clientY - p.startY;
            if (!p.started) {
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD) {
                    return;
                }
                // in the pool, horizontal touch moves are left to the scroll
                if (p.touch && p.fromSlot === undefined && Math.abs(dx) > Math.abs(dy)) {
                    pointer.current = undefined;
                    return;
                }
                p.started = true;
            }
            e.preventDefault();
            setDrag({gameId: p.gameId, x: e.clientX, y: e.clientY, over: slotUnder(e.clientX, e.clientY)});
        }

        function onUp(e) {
            const p = pointer.current;
            pointer.current = undefined;
            if (!p) {
                return;
            }
            setDrag(undefined);
            if (p.started) {
                move(p.gameId, p.fromSlot, slotUnder(e.clientX, e.clientY));
            } else if (p.fromSlot === undefined) {
                // tap in the pool: first empty slot
                const empty = [0, 1, 2].find(i => !podiumRef.current[i] || !gamesById[podiumRef.current[i]]);
                if (empty !== undefined) {
                    move(p.gameId, undefined, empty);
                }
            }
        }

        function onCancel() {
            pointer.current = undefined;
            setDrag(undefined);
        }

        window.addEventListener("pointermove", onMove, {passive: false});
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onCancel);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onCancel);
        };
    });

    function renderSlot(slot) {
        const gameId = slots[slot.index];
        const game = gameId ? gamesById[gameId] : undefined;
        const dragged = drag && drag.gameId === gameId;
        return (
            <div
                key={slot.index}
                data-slot={slot.index}
                className={"anniv2026-slot rank" + slot.index + (drag && drag.over === slot.index ? " over" : "") + (game ? " filled" : " empty")}
                onPointerDown={game ? (e) => onPointerDown(e, gameId, slot.index) : undefined}
            >
                <div className="anniv2026-slot-frame" style={dragged ? {opacity: 0.3} : {}}>
                    {
                        game ?
                            <img src={game.logo} alt={game.name} draggable={false}/> :
                            <div className="anniv2026-slot-empty">{slot.label}</div>
                    }
                </div>
                <div className="anniv2026-slot-label">{slot.label} · {slot.points}</div>
            </div>
        );
    }

    const dragGame = drag ? gamesById[drag.gameId] : undefined;

    return (
        <Dialog open={true} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm"
                PaperProps={{className: "anniv2026-dialog"}}>
            <div className="anniv2026-modal">
                <div className="anniv2026-modal-header">
                    <CategoryIcon category={category}/>
                    <div className="anniv2026-modal-title">{category.label}</div>
                    <IconButton className="anniv2026-modal-close" onClick={onClose}><CloseIcon/></IconButton>
                </div>

                <div className="anniv2026-podium">
                    <div className="anniv2026-podium-first">{renderSlot(SLOTS[0])}</div>
                    <div className="anniv2026-podium-others">
                        {renderSlot(SLOTS[1])}
                        {renderSlot(SLOTS[2])}
                    </div>
                </div>

                <div className="anniv2026-pool-help">
                    Glisse un jeu vers le haut sur le podium, ou touche-le pour le placer.
                    Sors un jeu du podium pour le retirer.
                </div>
                <div className="anniv2026-pool">
                    {
                        pool.map(game =>
                            <div key={game.id} className="anniv2026-card anniv2026-pool-item"
                                 onPointerDown={(e) => onPointerDown(e, game.id, undefined)}
                                 style={drag && drag.gameId === game.id ? {opacity: 0.3} : {}}>
                                <img src={game.logo} alt={game.name} draggable={false}/>
                                <div className="anniv2026-game-author">{game.authorName}</div>
                            </div>
                        )
                    }
                    {pool.length === 0 ? <div className="anniv2026-pool-empty">Tous les jeux sont placés.</div> : undefined}
                </div>
            </div>
            {
                dragGame ?
                    ReactDOM.createPortal(
                        <img className="anniv2026-drag-ghost" src={dragGame.logo} alt=""
                             style={{left: drag.x, top: drag.y}}/>,
                        document.body
                    ) :
                    undefined
            }
        </Dialog>
    );
}
